'use client'

import { useSyncExternalStore } from 'react'

export const RECENT_KEY = 'fridge_recently_viewed'
export const RECENT_DETAILS_KEY = 'fridge_recently_viewed_details'
export const RECENT_EVENT = 'fridge:recently-viewed'
export const RECENT_LIMIT = 12
export interface RecentProduct {
  id: string
  fin: string
  brand: string
  title: string
  href: string
  image: string
  viewedAt: number
}
const EMPTY: RecentProduct[] = []
let cachedRaw = ''
let cached: RecentProduct[] = EMPTY

/** Local presentation data only. Prices are deliberately not persisted. */
export function parseRecentProducts(raw: string | null): RecentProduct[] {
  try {
    const rows: unknown = JSON.parse(raw || '[]')
    if (!Array.isArray(rows)) return []
    const seen = new Set<string>()
    return rows.filter((p): p is RecentProduct => {
      if (!p || typeof p !== 'object') return false
      const r = p as RecentProduct
      if (![r.id, r.title, r.href, r.image].every(v => typeof v === 'string' && v.length > 0) ||
          typeof r.fin !== 'string' || typeof r.brand !== 'string' || !Number.isFinite(r.viewedAt) ||
          !/^\/(products|parts|filters)\/[^?#\s]+$/.test(r.href) || r.href.includes('..') ||
          !/^(\/[^/]|https:\/\/)/.test(r.image) || seen.has(r.id)) return false
      seen.add(r.id)
      return true
    }).slice(0, RECENT_LIMIT).map(({ id, fin, brand, title, href, image, viewedAt }) => ({ id, fin, brand, title, href, image, viewedAt }))
  } catch { return [] }
}
export function getRecentProducts(): RecentProduct[] {
  if (typeof window === 'undefined') return EMPTY
  try {
    const raw = localStorage.getItem(RECENT_DETAILS_KEY) || '[]'
    if (raw !== cachedRaw) { cachedRaw = raw; cached = parseRecentProducts(raw) }
    return cached
  } catch { return EMPTY }
}
function notify() { window.dispatchEvent(new Event(RECENT_EVENT)) }
export function recordRecentProduct(product: Omit<RecentProduct, 'viewedAt'>, now = Date.now()) {
  if (typeof window === 'undefined') return
  const rows = parseRecentProducts(JSON.stringify([{ ...product, viewedAt: now }, ...getRecentProducts().filter(p => p.id !== product.id && p.href !== product.href)]))
  if (!rows.some(p => p.id === product.id)) return
  try {
    localStorage.setItem(RECENT_DETAILS_KEY, JSON.stringify(rows))
    // Preserve the existing ID-key contract for other local seed readers.
    localStorage.setItem(RECENT_KEY, JSON.stringify(rows.map(p => p.id)))
    notify()
  } catch { /* Disabled or full storage must not break product browsing. */ }
}
export function clearRecentProducts() {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(RECENT_DETAILS_KEY)
    localStorage.removeItem(RECENT_KEY)
    localStorage.removeItem('fridge_recently_viewed_ids')
    notify()
  } catch { /* No account or remote fallback. */ }
}
function subscribe(cb: () => void) {
  const storage = (event: StorageEvent) => { if (!event.key || event.key === RECENT_DETAILS_KEY || event.key === RECENT_KEY) cb() }
  window.addEventListener('storage', storage)
  window.addEventListener(RECENT_EVENT, cb)
  return () => { window.removeEventListener('storage', storage); window.removeEventListener(RECENT_EVENT, cb) }
}
export function useRecentProducts() { return useSyncExternalStore(subscribe, getRecentProducts, () => EMPTY) }
